import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Plus, X, MapPin, Package, PackagePlus, ShoppingBag, Truck, CheckCircle, Lock, AlertTriangle, Star, Building2, GripVertical } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { Badge } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { useAuth } from '@contexts/AuthContext';
import { driverRouteService } from '@services/driverRouteService';
import { driverService } from '@services/driverService';
import { vehicleService } from '@services/vehicleService';
import { shippingProviderService } from '@services/shippingProviderService';
import eventService from '@services/eventService';
import { goodsDeliveryOrderService } from '@services/goodsDeliveryOrderService';
import { DeliveryOrder } from '@/types/event';
import { GoodsDeliveryOrder } from '@/types/goodsDeliveryOrder';
import { formatDateBR } from '@pages/financial/financialUtils';
import { getStopKey, stopRefId, stopRefLabel, stopTypeBadgeVariant, stopTypeLabel } from './driverRouteUtils';
import { DriverRoute, DriverRouteStop, DriverRouteStopFormData, SelectablePickupOrder, SelectableSalesOrder } from '@/types/driverRoute';
import { ShippingProvider } from '@/types/shippingProvider';
import { Driver } from '@/types/driver';
import { Vehicle } from '@/types/vehicle';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  // Strip Z/offset: backend returns Brasília local time, no UTC conversion should occur
  try { return new Date(dateStr.replace(/Z$|[+-]\d{2}:\d{2}$/, '')).toLocaleDateString('pt-BR'); } catch { return dateStr; }
};

const formatAddress = (item: {
  ship_addr_street?: string; ship_addr_num?: string; ship_addr_compl?: string;
  ship_addr_district?: string; ship_addr_city?: string; ship_addr_state?: string; ship_addr_zip?: string;
  address_street?: string; address_num?: string; address_compl?: string;
  address_district?: string; address_city?: string; address_state?: string; address_zip?: string;
}) => {
  const street = item.ship_addr_street || item.address_street || '';
  const num = item.ship_addr_num || item.address_num || '';
  const compl = item.ship_addr_compl || item.address_compl || '';
  const district = item.ship_addr_district || item.address_district || '';
  const city = item.ship_addr_city || item.address_city || '';
  const state = item.ship_addr_state || item.address_state || '';
  const parts = [street, num, compl, district, city, state].filter(Boolean);
  return parts.join(', ') || '—';
};

export const DriverRouteFormView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isEditMode = Boolean(id);
  const routeId = id ? Number(id) : null;

  const [saving, setSaving] = useState(false);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Driver and vehicle selection
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<number>(0);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number>(0);

  // Stops
  const [stops, setStops] = useState<(DriverRouteStopFormData & {
    _label: string;
    _address: string;
    _key: string;
    _isDelivered?: boolean;
    _deliveredAt?: string | null;
    _stopId?: number;
    _priority?: boolean;
    _shippingProviderId?: number | null;
  })[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Pending pickup orders inline
  const [pendingPickupOrders, setPendingPickupOrders] = useState<SelectablePickupOrder[]>([]);
  const [selectedPickupIds, setSelectedPickupIds] = useState<Set<number>>(new Set());
  const [pickupLoading, setPickupLoading] = useState(false);

  // Convites (Pedidos de Entrega) pendentes — logística própria
  const [pendingInvitations, setPendingInvitations] = useState<DeliveryOrder[]>([]);
  const [selectedInvitationIds, setSelectedInvitationIds] = useState<Set<number>>(new Set());
  const [invitationsLoading, setInvitationsLoading] = useState(false);

  // Ordens de Entrega avulsas pendentes (sem parada em rota)
  const [pendingDeliveryOrders, setPendingDeliveryOrders] = useState<GoodsDeliveryOrder[]>([]);
  const [selectedDeliveryOrderIds, setSelectedDeliveryOrderIds] = useState<Set<number>>(new Set());
  const [deliveryOrdersLoading, setDeliveryOrdersLoading] = useState(false);

  // Modals
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);

  // Pickup modal data (all orders, not just pending)
  const [allPickupOrders, setAllPickupOrders] = useState<SelectablePickupOrder[]>([]);
  const [allPickupLoading, setAllPickupLoading] = useState(false);
  const [allPickupPage, setAllPickupPage] = useState(1);
  const [allPickupTotalPages, setAllPickupTotalPages] = useState(1);

  // Sales modal data
  const [salesOrders, setSalesOrders] = useState<SelectableSalesOrder[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesPage, setSalesPage] = useState(1);
  const [salesTotalPages, setSalesTotalPages] = useState(1);
  const [selectedSalesIds, setSelectedSalesIds] = useState<Set<number>>(new Set());

  // Invoice warning modal
  const [showInvoiceWarning, setShowInvoiceWarning] = useState(false);
  const [ordersWithoutInvoice, setOrdersWithoutInvoice] = useState<SelectableSalesOrder[]>([]);

  // Shipping provider section
  const [shippingProviders, setShippingProviders] = useState<ShippingProvider[]>([]);
  const [shippingProvidersLoading, setShippingProvidersLoading] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [providerOrders, setProviderOrders] = useState<SelectableSalesOrder[]>([]);
  const [providerOrdersLoading, setProviderOrdersLoading] = useState(false);
  const [providerOrdersPage, setProviderOrdersPage] = useState(1);
  const [providerOrdersTotalPages, setProviderOrdersTotalPages] = useState(1);
  const [selectedProviderOrderIds, setSelectedProviderOrderIds] = useState<Set<number>>(new Set());

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [geocodingWarning, setGeocodingWarning] = useState<string | null>(null);
  const [geocodingFailedStops, setGeocodingFailedStops] = useState<DriverRoute['stops']>([]);
  const [savedRouteId, setSavedRouteId] = useState<number | null>(null);

  // Load drivers, vehicles, and check if user is a driver
  useEffect(() => {
    const init = async () => {
      try {
        const [driversResult, vehiclesResult] = await Promise.all([
          driverService.getAll({ page: 1, pageSize: 100 }),
          // só veículos que podem fazer rota: veículo da diretoria só se liberado
          vehicleService.getAll({ page: 1, pageSize: 100, for_route: true }),
        ]);
        setDrivers(driversResult.data);
        setVehicles(vehiclesResult.data);

        // In edit mode, load the existing route
        if (routeId) {
          setLoadingRoute(true);
          const route = await driverRouteService.getById(routeId);
          setSelectedDriverId(route.driver_id);
          setSelectedVehicleId(route.vehicle_id);
          // a rota que já usa um veículo fora da lista (ex.: diretoria desliberada) continua editável
          const routeVehicle = route.vehicle;
          if (routeVehicle && !vehiclesResult.data.some((v) => v.vehicle_id === route.vehicle_id)) {
            setVehicles([...vehiclesResult.data, routeVehicle]);
          }

          // Map existing stops
          if (route.stops) {
            const mappedStops = route.stops.map((s) => {
              const refId = stopRefId(s);
              return {
                route_stop_type: s.route_stop_type,
                order_id: s.order_id ?? null,
                goods_pickup_orders_id: s.goods_pickup_orders_id ?? null,
                delivery_order_id: s.delivery_order_id ?? null,
                goods_delivery_orders_id: s.goods_delivery_orders_id ?? null,
                _label: `${stopTypeLabel(s.route_stop_type)} #${refId}${s.customer_name ? ` — ${s.customer_name}` : (s.reference_description ? ` — ${s.reference_description}` : '')}${s.suspended_at ? ' (entrega suspensa)' : ''}`,
                _address: formatAddress(s),
                _key: getStopKey(s),
                // Parada suspensa (RN-ROTA-48) fica travada como a entregue: é histórico da rota.
                _isDelivered: s.is_delivered || !!s.suspended_at,
                _deliveredAt: s.delivered_at,
                _stopId: s.driver_route_stop_id,
                _priority: s.priority ?? false,
              };
            });
            setStops(mappedStops);
          }
          setLoadingRoute(false);
        } else {
          // Create mode — do NOT pre-select any driver, leave the list free
        }
      } catch (err) {
        if (routeId) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar rota');
          setLoadingRoute(false);
        }
      }
    };
    init();
  }, [user?.email, routeId]);

  // Load pending pickup orders for inline display
  useEffect(() => {
    loadPendingPickupOrders();
    loadShippingProviders();
    loadPendingInvitations();
    loadPendingDeliveryOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPendingPickupOrders = async () => {
    try {
      setPickupLoading(true);
      const result = await driverRouteService.getPendingPickupOrders(1, 50);
      setPendingPickupOrders(result.data);
    } catch {
      // Silent
    } finally {
      setPickupLoading(false);
    }
  };

  // Convites (Pedidos de Entrega) pendentes de logística própria. A pré-seleção via
  // nav state vem do Planejamento de Rota (Eventos → Planejamento de Rota).
  const loadPendingInvitations = async () => {
    try {
      setInvitationsLoading(true);
      const list = await eventService.getPendingForRoute();
      setPendingInvitations(list);
      const preselected = (location.state as { preselectedDeliveryOrderIds?: number[] } | null)?.preselectedDeliveryOrderIds;
      if (preselected && preselected.length > 0) {
        const valid = new Set(list.map((d) => d.delivery_order_id));
        const newStops = preselected
          .filter((id) => valid.has(id))
          .map((id) => {
            const inv = list.find((d) => d.delivery_order_id === id);
            return {
              route_stop_type: 'D' as const,
              order_id: null,
              goods_pickup_orders_id: null,
              delivery_order_id: id,
              _label: `Convite #${id}${inv?.recipient_name ? ` — ${inv.recipient_name}` : ''}`,
              _address: inv ? formatAddress(inv) : '—',
              _key: `D-${id}`,
            };
          });
        setStops((prev) => [...prev, ...newStops.filter((n) => !prev.some((p) => p._key === n._key))]);
      }
    } catch {
      // Silent — o painel mostra vazio
    } finally {
      setInvitationsLoading(false);
    }
  };

  const toggleInvitationSelection = (id: number) => {
    setSelectedInvitationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const addInvitationStops = () => {
    const newStops = Array.from(selectedInvitationIds)
      .filter((id) => !stops.some((s) => s.delivery_order_id === id))
      .map((id) => {
        const inv = pendingInvitations.find((d) => d.delivery_order_id === id);
        return {
          route_stop_type: 'D' as const,
          order_id: null,
          goods_pickup_orders_id: null,
          delivery_order_id: id,
          _label: `Convite #${id}${inv?.recipient_name ? ` — ${inv.recipient_name}` : ''}`,
          _address: inv ? formatAddress(inv) : '—',
          _key: `D-${id}`,
        };
      });
    setStops((prev) => [...prev, ...newStops]);
    setSelectedInvitationIds(new Set());
  };

  // Ordens de Entrega avulsas: o backend já exclui canceladas, entregues e as que têm parada em rota.
  const loadPendingDeliveryOrders = async () => {
    try {
      setDeliveryOrdersLoading(true);
      setPendingDeliveryOrders(await goodsDeliveryOrderService.getPendingForRoute());
    } catch {
      // Silent — o painel mostra vazio (ex.: usuário sem permissão)
    } finally {
      setDeliveryOrdersLoading(false);
    }
  };

  const toggleDeliveryOrderSelection = (id: number) => {
    setSelectedDeliveryOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Emergência entra na rota já como prioridade (o backend também grava priority=true).
  const addDeliveryOrderStops = () => {
    const newStops = Array.from(selectedDeliveryOrderIds)
      .filter((id) => !stops.some((s) => s.route_stop_type === 'O' && s.goods_delivery_orders_id === id))
      .map((id) => {
        const ord = pendingDeliveryOrders.find((o) => o.goods_delivery_orders_id === id);
        return {
          route_stop_type: 'O' as const,
          order_id: null,
          goods_pickup_orders_id: null,
          delivery_order_id: null,
          goods_delivery_orders_id: id,
          _label: `Ordem de Entrega #${id}${ord?.recipient_name ? ` — ${ord.recipient_name}` : ''}`,
          _address: ord ? formatAddress(ord) : '—',
          _key: `O-${id}`,
          _priority: Boolean(ord?.is_emergency),
        };
      });
    setStops((prev) => [...prev, ...newStops]);
    setSelectedDeliveryOrderIds(new Set());
  };

  // Load all pickup orders for modal
  const loadAllPickupOrders = async (page: number = 1) => {
    try {
      setAllPickupLoading(true);
      const result = await driverRouteService.getPendingPickupOrders(page, 20);
      setAllPickupOrders(result.data);
      setAllPickupPage(page);
      setAllPickupTotalPages(result.pagination.totalPages);
    } catch {
      // Silent
    } finally {
      setAllPickupLoading(false);
    }
  };

  // Load sales orders for modal
  const loadSalesOrders = async (page: number = 1) => {
    try {
      setSalesLoading(true);
      const result = await driverRouteService.getDeliverySalesOrders(page, 20);
      setSalesOrders(result.data);
      setSalesPage(page);
      setSalesTotalPages(result.pagination.totalPages);
    } catch {
      // Silent
    } finally {
      setSalesLoading(false);
    }
  };

  // Load shipping providers with address
  const loadShippingProviders = async () => {
    try {
      setShippingProvidersLoading(true);
      const result = await shippingProviderService.getProviders({ hasAddress: true, pageSize: 100 });
      setShippingProviders(result.data);
    } catch {
      // Silent
    } finally {
      setShippingProvidersLoading(false);
    }
  };

  // Load orders for a specific shipping provider
  const loadProviderOrders = async (providerId: number, page: number = 1) => {
    try {
      setProviderOrdersLoading(true);
      const result = await driverRouteService.getOrdersByShippingProvider(providerId, page, 20);
      setProviderOrders(result.data);
      setProviderOrdersPage(page);
      setProviderOrdersTotalPages(result.pagination.totalPages);
    } catch {
      setProviderOrders([]);
    } finally {
      setProviderOrdersLoading(false);
    }
  };

  // Handle provider selection
  const handleProviderSelect = (providerId: number) => {
    if (selectedProviderId === providerId) {
      setSelectedProviderId(null);
      setProviderOrders([]);
      setSelectedProviderOrderIds(new Set());
    } else {
      setSelectedProviderId(providerId);
      setSelectedProviderOrderIds(new Set());
      loadProviderOrders(providerId);
    }
  };

  const toggleProviderOrderSelection = (id: number) => {
    setSelectedProviderOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Add selected provider orders as stops (with shipping_provider_id)
  const addProviderOrderStops = () => {
    if (!selectedProviderId) return;
    const newStops = Array.from(selectedProviderOrderIds)
      .filter((id) => !stops.some((s) => s.order_id === id))
      .map((id) => {
        const order = providerOrders.find((o) => o.order_id === id);
        return {
          route_stop_type: 'E' as const,
          order_id: id,
          goods_pickup_orders_id: null,
          shipping_provider_id: selectedProviderId,
          _label: `Entrega #${id}${order?.customer_name ? ` — ${order.customer_name}` : ''}`,
          _address: order ? formatAddress(order) : '—',
          _key: `E-${id}`,
          _shippingProviderId: selectedProviderId,
        };
      });
    setStops((prev) => [...prev, ...newStops]);
    setSelectedProviderOrderIds(new Set());
  };

  const togglePickupSelection = (id: number) => {
    setSelectedPickupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSalesSelection = (id: number) => {
    setSelectedSalesIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Add selected pickup orders as stops
  const addPickupStops = () => {
    const allOrders = [...pendingPickupOrders, ...allPickupOrders];
    const newStops = Array.from(selectedPickupIds)
      .filter((id) => !stops.some((s) => s.goods_pickup_orders_id === id))
      .map((id) => {
        const order = allOrders.find((o) => o.goods_pickup_orders_id === id);
        return {
          route_stop_type: 'R' as const,
          goods_pickup_orders_id: id,
          order_id: null,
          _label: `Retirada #${id}${order ? ` — ${order.description}` : ''}`,
          _address: order ? formatAddress(order) : '—',
          _key: `R-${id}`,
        };
      });
    setStops((prev) => [...prev, ...newStops]);
    setSelectedPickupIds(new Set());
  };

  // Add selected sales orders as stops
  const addSalesStops = () => {
    // Check for orders without invoice
    const selectedOrdersList = Array.from(selectedSalesIds)
      .filter((id) => !stops.some((s) => s.order_id === id))
      .map((id) => salesOrders.find((o) => o.order_id === id))
      .filter((o): o is SelectableSalesOrder => !!o);

    const withoutInvoice = selectedOrdersList.filter((o) => !o.invoice_id);

    if (withoutInvoice.length > 0) {
      setOrdersWithoutInvoice(withoutInvoice);
      setShowInvoiceWarning(true);
      return;
    }

    confirmAddSalesStops();
  };

  const confirmAddSalesStops = () => {
    const newStops = Array.from(selectedSalesIds)
      .filter((id) => !stops.some((s) => s.order_id === id))
      .map((id) => {
        const order = salesOrders.find((o) => o.order_id === id);
        return {
          route_stop_type: 'E' as const,
          order_id: id,
          goods_pickup_orders_id: null,
          _label: `Entrega #${id}${order?.customer_name ? ` — ${order.customer_name}` : ''}`,
          _address: order ? formatAddress(order) : '—',
          _key: `E-${id}`,
          _priority: Boolean(order?.is_priority),
        };
      });
    setStops((prev) => [...prev, ...newStops]);
    setSelectedSalesIds(new Set());
    setShowSalesModal(false);
    setShowInvoiceWarning(false);
    setOrdersWithoutInvoice([]);
  };

  const removeStop = (key: string) => {
    setStops((prev) => prev.filter((s) => s._key !== key || s._isDelivered));
  };

  const toggleStopPriority = (key: string) => {
    setStops((prev) => prev.map((s) => s._key === key ? { ...s, _priority: !s._priority } : s));
  };

  const moveStop = (fromIndex: number, toIndex: number) => {
    setStops((prev) => {
      if (fromIndex === toIndex) return prev;
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) return prev;
      if (prev[fromIndex]?._isDelivered || prev[toIndex]?._isDelivered) return prev;

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!selectedDriverId) newErrors.driver_id = 'Selecione um motorista';
    if (!selectedVehicleId) newErrors.vehicle_id = 'Selecione um veículo';
    const hasAnyStop = stops.length > 0;
    if (!hasAnyStop) newErrors.stops = 'Adicione ao menos uma parada';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError(null);
      setGeocodingWarning(null);

      const stopsPayload = stops.map((s) => ({
        route_stop_type: s.route_stop_type,
        order_id: s.order_id,
        goods_pickup_orders_id: s.goods_pickup_orders_id,
        delivery_order_id: s.delivery_order_id,
        goods_delivery_orders_id: s.goods_delivery_orders_id,
        priority: s._priority ?? false,
        shipping_provider_id: s._shippingProviderId ?? null,
        ...(s._stopId ? { driver_route_stop_id: s._stopId } : {}),
        ...(s._isDelivered !== undefined ? { is_delivered: s._isDelivered } : {}),
      }));

      let result: DriverRoute;
      if (isEditMode && routeId) {
        result = await driverRouteService.update(routeId, {
          driver_id: selectedDriverId,
          vehicle_id: selectedVehicleId,
          stops: stopsPayload,
        });
      } else {
        result = await driverRouteService.create({
          driver_id: selectedDriverId,
          vehicle_id: selectedVehicleId,
          stops: stopsPayload,
        });
      }

      const reorderedStopIds = stops
        .map((stop) => {
          if (stop._stopId) {
            return stop._stopId;
          }

          const persistedStop = result.stops?.find((savedStop: DriverRouteStop) => getStopKey(savedStop) === stop._key);
          return persistedStop?.driver_route_stop_id;
        })
        .filter((stopId): stopId is number => typeof stopId === 'number');

      if (reorderedStopIds.length === stops.length && reorderedStopIds.length > 1) {
        result = await driverRouteService.reorderStops(result.driver_route_id, reorderedStopIds);
      }

      // Auto-otimização: quando criando uma nova rota com 2+ paradas geocodificadas
      if (!isEditMode && result.stops && result.stops.length >= 2) {
        const allGeocoded = result.stops.every(
          (s) => s.latitude != null && s.longitude != null,
        );
        if (allGeocoded) {
          try {
            result = await driverRouteService.optimizeRoute(result.driver_route_id);
          } catch (optErr) {
            console.warn('Auto-otimização falhou (pode ser re-tentada manualmente):', optErr);
          }
        }
      }

      // Verificar paradas com falha de geocodificação
      const failedStops = result.stops?.filter(
        (stop) => stop.geocoding_status === 1
      );
      if (failedStops && failedStops.length > 0) {
        setGeocodingWarning(
          `${failedStops.length} parada(s) não puderam ser geocodificadas. Os endereços devem ser revisados ou as coordenadas informadas manualmente.`
        );
        setGeocodingFailedStops(failedStops);
        setSavedRouteId(result.driver_route_id);
        // Não navegar imediatamente — permitir que o usuário veja o aviso
        return;
      }

      navigate('/driver-routes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loadingRoute) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/driver-routes')} icon={<ArrowLeft size={20} />} />
          <h1 className="text-h1">Carregando rota...</h1>
        </div>
        <Card>
          <CardContent>
            <div className="text-center py-12 text-slate-500">Carregando dados da rota...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/driver-routes')} icon={<ArrowLeft size={20} />} />
        <div>
          <h1 className="text-h1">{isEditMode ? `Editar Rota #${routeId}` : 'Nova Rota de Entrega'}</h1>
          <p className="text-slate-600">{isEditMode ? 'Altere os dados da rota. Paradas já entregues não podem ser removidas.' : 'Monte a rota selecionando o motorista, veículo e as paradas'}</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg sticky top-4 z-10">{error}</div>
      )}

      {geocodingWarning && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg sticky top-4 z-10">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Aviso de Geolocalização</p>
              <p className="text-sm mt-1">{geocodingWarning}</p>
              {geocodingFailedStops && geocodingFailedStops.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {geocodingFailedStops.map((stop) => (
                    <div key={stop.driver_route_stop_id} className="flex items-center gap-2 text-sm bg-white border border-amber-100 rounded px-3 py-1.5">
                      <MapPin size={12} className="text-amber-500 flex-shrink-0" />
                      <span className="font-medium text-slate-700">
                        {stopRefLabel(stop)}
                      </span>
                      <span className="text-xs text-slate-500 truncate">
                        {formatAddress(stop)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button variant="secondary" size="sm" onClick={() => {
              if (savedRouteId) {
                navigate(`/driver-routes/${savedRouteId}`);
              } else {
                navigate('/driver-routes');
              }
            }}>
              {savedRouteId ? 'Ir para a rota' : 'Continuar'}
            </Button>
          </div>
        </div>
      )}

      {/* Driver & Vehicle */}
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><Truck size={20} /> Motorista e Veículo</span>}
        />
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Motorista *</label>
              <select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(Number(e.target.value))}
                className={`w-full rounded-lg border px-3 py-2 text-sm ${errors.driver_id ? 'border-red-500' : 'border-slate-300'}`}
              >
                <option value={0}>Selecione um motorista</option>
                {drivers.filter((d) => d.is_active).map((d) => (
                  <option key={d.driver_id} value={d.driver_id}>{d.full_name}</option>
                ))}
              </select>
              {errors.driver_id && <p className="text-red-500 text-sm mt-1">{errors.driver_id}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Veículo *</label>
              <select
                value={selectedVehicleId}
                onChange={(e) => setSelectedVehicleId(Number(e.target.value))}
                className={`w-full rounded-lg border px-3 py-2 text-sm ${errors.vehicle_id ? 'border-red-500' : 'border-slate-300'}`}
              >
                <option value={0}>Selecione um veículo</option>
                {vehicles.map((v) => (
                  <option key={v.vehicle_id} value={v.vehicle_id}>{v.description} — {v.license_plate}</option>
                ))}
              </select>
              {errors.vehicle_id && <p className="text-red-500 text-sm mt-1">{errors.vehicle_id}</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Pickup Orders (inline) */}
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><Package size={20} /> Ordens de Retirada Pendentes</span>}
          action={
            <div className="flex gap-2">
              {selectedPickupIds.size > 0 && (
                <Button size="sm" onClick={addPickupStops} icon={<Plus size={16} />}>
                  Adicionar {selectedPickupIds.size} selecionada(s)
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { loadAllPickupOrders(); setShowPickupModal(true); }}
              >
                Consultar Mais
              </Button>
            </div>
          }
        />
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th style={{width: '40px'}}></th>
                <th className="text-left">ID</th>
                <th className="text-left">Descrição</th>
                <th className="text-left">Endereço</th>
                <th className="text-left">Prazo</th>
              </tr>
            </TableHeader>
            <tbody>
              {pickupLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6 text-slate-500">Carregando...</TableCell>
                </TableRow>
              ) : pendingPickupOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6 text-slate-500">Nenhuma ordem pendente</TableCell>
                </TableRow>
              ) : (
                pendingPickupOrders.map((order) => {
                  const alreadyAdded = stops.some((s) => s.goods_pickup_orders_id === order.goods_pickup_orders_id);
                  return (
                    <TableRow key={order.goods_pickup_orders_id} className={alreadyAdded ? 'opacity-50' : ''}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedPickupIds.has(order.goods_pickup_orders_id) || alreadyAdded}
                          disabled={alreadyAdded}
                          onChange={() => togglePickupSelection(order.goods_pickup_orders_id)}
                          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{order.goods_pickup_orders_id}</TableCell>
                      <TableCell className="font-medium">{order.description}</TableCell>
                      <TableCell className="text-sm text-slate-600">{formatAddress(order)}</TableCell>
                      <TableCell>{formatDate(order.pickup_deadline_date)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {/* Convites (Pedidos de Entrega) pendentes — logística própria */}
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><Package size={20} /> Convites Pendentes (Eventos)</span>}
          action={
            selectedInvitationIds.size > 0 ? (
              <Button size="sm" onClick={addInvitationStops} icon={<Plus size={16} />}>
                Adicionar {selectedInvitationIds.size} selecionado(s)
              </Button>
            ) : undefined
          }
        />
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th style={{width: '40px'}}></th>
                <th className="text-left">ID</th>
                <th className="text-left">Convidado</th>
                <th className="text-left">Evento</th>
                <th className="text-left">Endereço</th>
              </tr>
            </TableHeader>
            <tbody>
              {invitationsLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6 text-slate-500">Carregando...</TableCell>
                </TableRow>
              ) : pendingInvitations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6 text-slate-500">Nenhum convite pendente</TableCell>
                </TableRow>
              ) : (
                pendingInvitations.map((inv) => {
                  const alreadyAdded = stops.some((s) => s.delivery_order_id === inv.delivery_order_id);
                  return (
                    <TableRow key={inv.delivery_order_id} className={alreadyAdded ? 'opacity-50' : ''}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedInvitationIds.has(inv.delivery_order_id) || alreadyAdded}
                          disabled={alreadyAdded}
                          onChange={() => toggleInvitationSelection(inv.delivery_order_id)}
                          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{inv.delivery_order_id}</TableCell>
                      <TableCell className="font-medium">{inv.recipient_name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{inv.event_name || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{formatAddress(inv)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {/* Ordens de Entrega avulsas pendentes */}
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><PackagePlus size={20} /> Ordens de Entrega Pendentes</span>}
          action={
            selectedDeliveryOrderIds.size > 0 ? (
              <Button size="sm" onClick={addDeliveryOrderStops} icon={<Plus size={16} />}>
                Adicionar {selectedDeliveryOrderIds.size} selecionada(s)
              </Button>
            ) : undefined
          }
        />
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th style={{width: '40px'}}></th>
                <th className="text-left">ID</th>
                <th className="text-left">Destinatário</th>
                <th className="text-left">Descrição</th>
                <th className="text-left">Endereço</th>
                <th className="text-left">Data prevista</th>
              </tr>
            </TableHeader>
            <tbody>
              {deliveryOrdersLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-slate-500">Carregando...</TableCell>
                </TableRow>
              ) : pendingDeliveryOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-slate-500">Nenhuma ordem de entrega pendente</TableCell>
                </TableRow>
              ) : (
                pendingDeliveryOrders.map((ord) => {
                  const alreadyAdded = stops.some((s) => s.route_stop_type === 'O' && s.goods_delivery_orders_id === ord.goods_delivery_orders_id);
                  return (
                    <TableRow key={ord.goods_delivery_orders_id} className={alreadyAdded ? 'opacity-50' : ''}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedDeliveryOrderIds.has(ord.goods_delivery_orders_id) || alreadyAdded}
                          disabled={alreadyAdded}
                          onChange={() => toggleDeliveryOrderSelection(ord.goods_delivery_orders_id)}
                          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{ord.goods_delivery_orders_id}</TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2 flex-wrap">
                          {ord.recipient_name}
                          {ord.is_emergency && (
                            <Badge variant="danger" size="sm" className="gap-1">
                              <AlertTriangle size={10} /> Emergência
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{ord.description}</TableCell>
                      <TableCell className="text-sm text-slate-600">{formatAddress(ord)}</TableCell>
                      <TableCell>{formatDateBR(ord.expected_delivery_date) || '—'}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {/* Shipping Provider Orders */}
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><Building2 size={20} /> Pedidos por Transportadora</span>}
        />
        <CardContent className="space-y-4">
          {shippingProvidersLoading ? (
            <p className="text-sm text-slate-500 text-center py-4">Carregando transportadoras...</p>
          ) : shippingProviders.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Nenhuma transportadora com endereço cadastrado.</p>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Selecione uma transportadora com endereço de coleta:</label>
                <div className="flex flex-wrap gap-2">
                  {shippingProviders.map((sp) => (
                    <button
                      key={sp.provider_id}
                      type="button"
                      onClick={() => handleProviderSelect(sp.provider_id)}
                      className={`px-3 py-2 text-sm border rounded-lg transition-colors ${
                        selectedProviderId === sp.provider_id
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-800 font-medium'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span className="font-medium">{sp.provider_name}</span>
                      <span className="block text-xs text-slate-500 mt-0.5 text-left">
                        {[sp.ship_addr_city, sp.ship_addr_state].filter(Boolean).join('/') || 'Endereço cadastrado'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {selectedProviderId && (
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-slate-700">
                      Pedidos com frete via {shippingProviders.find(p => p.provider_id === selectedProviderId)?.provider_name}
                    </h4>
                    {selectedProviderOrderIds.size > 0 && (
                      <Button size="sm" onClick={addProviderOrderStops} icon={<Plus size={14} />}>
                        Adicionar {selectedProviderOrderIds.size} pedido(s)
                      </Button>
                    )}
                  </div>
                  {providerOrdersLoading ? (
                    <p className="text-sm text-slate-500 text-center py-6">Carregando pedidos...</p>
                  ) : providerOrders.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6">Nenhum pedido encontrado com esta transportadora selecionada.</p>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <tr>
                            <th style={{width: '40px'}}></th>
                            <th className="text-left">Pedido</th>
                            <th className="text-left">Cliente</th>
                            <th className="text-center">Status</th>
                            <th className="text-left">Endereço</th>
                            <th className="text-left">Atualização</th>
                          </tr>
                        </TableHeader>
                        <tbody>
                          {providerOrders.map((order) => {
                            const alreadyAdded = stops.some((s) => s.order_id === order.order_id);
                            return (
                              <TableRow key={order.order_id} className={alreadyAdded ? 'opacity-50' : ''}>
                                <TableCell>
                                  <input
                                    type="checkbox"
                                    checked={selectedProviderOrderIds.has(order.order_id) || alreadyAdded}
                                    disabled={alreadyAdded}
                                    onChange={() => toggleProviderOrderSelection(order.order_id)}
                                    className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                  />
                                </TableCell>
                                <TableCell className="font-mono text-sm font-medium">#{order.order_id}</TableCell>
                                <TableCell>{order.customer_name || '—'}</TableCell>
                                <TableCell>
                                  <Badge variant={order.order_status_id === 6 ? 'info' : 'success'}>
                                    {order.order_status || (order.order_status_id === 6 ? 'Conferido' : 'Atendido')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm text-slate-600">{formatAddress(order)}</TableCell>
                                <TableCell>{formatDate(order.updated_at)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </tbody>
                      </Table>
                      {providerOrdersTotalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-3">
                          <Button variant="secondary" size="sm" disabled={providerOrdersPage <= 1} onClick={() => loadProviderOrders(selectedProviderId!, providerOrdersPage - 1)}>
                            Anterior
                          </Button>
                          <span className="text-sm text-slate-500">Pág {providerOrdersPage}/{providerOrdersTotalPages}</span>
                          <Button variant="secondary" size="sm" disabled={providerOrdersPage >= providerOrdersTotalPages} onClick={() => loadProviderOrders(selectedProviderId!, providerOrdersPage + 1)}>
                            Próxima
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Route Stops */}
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><MapPin size={20} /> Paradas da Rota ({stops.length})</span>}
          action={
            <PermissionGuard permission="driver_routes.write">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { loadSalesOrders(); setShowSalesModal(true); }}
                icon={<ShoppingBag size={16} />}
              >
                Adicionar Pedidos de Venda
              </Button>
            </PermissionGuard>
          }
        />
        <CardContent>
          {errors.stops && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mb-4">{errors.stops}</div>
          )}
          {stops.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <MapPin size={40} className="mx-auto mb-2 text-slate-300" />
              <p>Nenhuma parada adicionada</p>
              <p className="text-sm">Selecione ordens de retirada acima ou adicione pedidos de venda</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Arraste as paradas pendentes pelo indicador lateral para alterar a ordem antes de salvar a rota.
              </p>
              {stops.map((stop, idx) => (
                <div
                  key={stop._key}
                  draggable={!stop._isDelivered}
                  onDragStart={(e) => {
                    if (stop._isDelivered) return;
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', stop._key);
                    setDragIndex(idx);
                  }}
                  onDragOver={(e) => {
                    if (stop._isDelivered) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDragOverIndex(idx);
                  }}
                  onDragLeave={() => {
                    if (dragOverIndex === idx) {
                      setDragOverIndex(null);
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex === null) return;
                    moveStop(dragIndex, idx);
                  }}
                  onDragEnd={() => {
                    setDragIndex(null);
                    setDragOverIndex(null);
                  }}
                  className={`flex items-start gap-3 p-3 border rounded-lg transition-all ${dragOverIndex === idx && !stop._isDelivered ? 'border-emerald-400 bg-emerald-50 shadow-sm' : ''} ${stop._isDelivered ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 hover:bg-slate-50'} ${dragIndex === idx ? 'opacity-60' : ''}`}
                >
                  {!stop._isDelivered && (
                    <div className="flex-shrink-0 pt-1 text-slate-400 cursor-grab active:cursor-grabbing">
                      <GripVertical size={16} />
                    </div>
                  )}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${stop._isDelivered ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {stop._isDelivered ? <CheckCircle size={16} /> : idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant={stopTypeBadgeVariant(stop.route_stop_type)}>
                        {stopTypeLabel(stop.route_stop_type)}
                      </Badge>
                      {stop._isDelivered && (
                        <Badge variant="success">Entregue</Badge>
                      )}
                      {stop._priority && (
                        <Badge variant="danger">★ Prioridade</Badge>
                      )}
                      {stop._shippingProviderId && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full">
                          <Building2 size={10} /> {shippingProviders.find(p => p.provider_id === stop._shippingProviderId)?.provider_name || 'Transportadora'}
                        </span>
                      )}
                      <span className="font-medium text-sm truncate">{stop._label}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                      <MapPin size={12} /> {stop._address}
                    </p>
                    {stop._isDelivered && stop._deliveredAt && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Entregue em: {new Date(stop._deliveredAt).toLocaleString('pt-BR')}
                      </p>
                    )}
                  </div>
                  {stop._isDelivered ? (
                    <div className="flex-shrink-0 p-2 text-slate-300" title="Parada já entregue — não pode ser removida">
                      <Lock size={16} />
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => toggleStopPriority(stop._key)}
                        className={`p-2 rounded-lg transition-colors ${stop._priority ? 'text-amber-500 bg-amber-50 hover:bg-amber-100' : 'text-slate-300 hover:text-amber-400 hover:bg-slate-100'}`}
                        title={stop._priority ? 'Remover prioridade' : 'Marcar como prioridade'}
                      >
                        <Star size={16} fill={stop._priority ? 'currentColor' : 'none'} />
                      </button>
                      <Button variant="secondary" size="sm" onClick={() => removeStop(stop._key)} icon={<X size={16} className="text-red-500" />} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={() => navigate('/driver-routes')}>Cancelar</Button>
        <Button onClick={handleSave} disabled={saving} icon={<Save size={18} />}>
          {saving ? 'Salvando...' : (isEditMode ? 'Atualizar Rota' : 'Salvar Rota')}
        </Button>
      </div>

      {/* Pickup Orders Modal */}
      {showPickupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Ordens de Retirada</h3>
              <Button variant="secondary" size="sm" onClick={() => setShowPickupModal(false)} icon={<X size={20} />} />
            </div>
            <div className="flex-1 overflow-auto p-0">
              <Table>
                <TableHeader>
                  <tr>
                    <th style={{width: '40px'}}></th>
                    <th className="text-left">ID</th>
                    <th className="text-left">Descrição</th>
                    <th className="text-left">Endereço</th>
                    <th className="text-left">Prazo</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {allPickupLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-slate-500">Carregando...</TableCell>
                    </TableRow>
                  ) : allPickupOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-slate-500">Nenhuma ordem encontrada</TableCell>
                    </TableRow>
                  ) : (
                    allPickupOrders.map((order) => {
                      const alreadyAdded = stops.some((s) => s.goods_pickup_orders_id === order.goods_pickup_orders_id);
                      return (
                        <TableRow key={order.goods_pickup_orders_id} className={alreadyAdded ? 'opacity-50' : ''}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedPickupIds.has(order.goods_pickup_orders_id) || alreadyAdded}
                              disabled={alreadyAdded}
                              onChange={() => togglePickupSelection(order.goods_pickup_orders_id)}
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">{order.goods_pickup_orders_id}</TableCell>
                          <TableCell className="font-medium">{order.description}</TableCell>
                          <TableCell className="text-sm text-slate-600">{formatAddress(order)}</TableCell>
                          <TableCell>{formatDate(order.pickup_deadline_date)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
            <div className="p-4 border-t flex items-center justify-between">
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={allPickupPage <= 1} onClick={() => loadAllPickupOrders(allPickupPage - 1)}>
                  Anterior
                </Button>
                <span className="text-sm text-slate-500 flex items-center px-2">Pág {allPickupPage}/{allPickupTotalPages}</span>
                <Button variant="secondary" size="sm" disabled={allPickupPage >= allPickupTotalPages} onClick={() => loadAllPickupOrders(allPickupPage + 1)}>
                  Próxima
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setShowPickupModal(false)}>Fechar</Button>
                {selectedPickupIds.size > 0 && (
                  <Button onClick={() => { addPickupStops(); setShowPickupModal(false); }}>
                    Adicionar {selectedPickupIds.size}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Orders Modal */}
      {showSalesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Pedidos de Venda — Conferido / Atendido</h3>
              <Button variant="secondary" size="sm" onClick={() => setShowSalesModal(false)} icon={<X size={20} />} />
            </div>
            <div className="flex-1 overflow-auto p-0">
              <Table>
                <TableHeader>
                  <tr>
                    <th style={{width: '40px'}}></th>
                    <th className="text-left">Pedido</th>
                    <th className="text-left">Cliente</th>
                    <th className="text-center">Status</th>
                    <th className="text-left">Endereço</th>
                    <th className="text-left">Atualização</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {salesLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-slate-500">Carregando...</TableCell>
                    </TableRow>
                  ) : salesOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-slate-500">Nenhum pedido encontrado</TableCell>
                    </TableRow>
                  ) : (
                    salesOrders.map((order) => {
                      const alreadyAdded = stops.some((s) => s.order_id === order.order_id);
                      return (
                        <TableRow key={order.order_id} className={alreadyAdded ? 'opacity-50' : ''}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedSalesIds.has(order.order_id) || alreadyAdded}
                              disabled={alreadyAdded}
                              onChange={() => toggleSalesSelection(order.order_id)}
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm font-medium">#{order.order_id}</TableCell>
                          <TableCell>{order.customer_name || '—'}</TableCell>
                          <TableCell>
                            <Badge variant={order.order_status_id === 6 ? 'info' : 'success'}>
                              {order.order_status || (order.order_status_id === 6 ? 'Conferido' : 'Atendido')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{formatAddress(order)}</TableCell>
                          <TableCell>{formatDate(order.updated_at)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
            <div className="p-4 border-t flex items-center justify-between">
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={salesPage <= 1} onClick={() => loadSalesOrders(salesPage - 1)}>
                  Anterior
                </Button>
                <span className="text-sm text-slate-500 flex items-center px-2">Pág {salesPage}/{salesTotalPages}</span>
                <Button variant="secondary" size="sm" disabled={salesPage >= salesTotalPages} onClick={() => loadSalesOrders(salesPage + 1)}>
                  Próxima
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setShowSalesModal(false)}>Fechar</Button>
                {selectedSalesIds.size > 0 && (
                  <Button onClick={addSalesStops}>
                    Adicionar {selectedSalesIds.size} pedido(s)
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Warning Modal */}
      {showInvoiceWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center gap-3 p-4 border-b bg-amber-50 rounded-t-xl">
              <AlertTriangle size={24} className="text-amber-500 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-amber-800">Pedidos sem Nota Fiscal</h3>
                <p className="text-sm text-amber-600">Os seguintes pedidos não possuem nota fiscal emitida:</p>
              </div>
            </div>
            <div className="p-4">
              <div className="space-y-2 mb-4 max-h-40 overflow-auto">
                {ordersWithoutInvoice.map((order) => (
                  <div key={order.order_id} className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-100 rounded px-3 py-2">
                    <Package size={14} className="text-amber-500 flex-shrink-0" />
                    <span className="font-medium">Pedido #{order.order_id}</span>
                    {order.customer_name && <span className="text-slate-500">— {order.customer_name}</span>}
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Deseja continuar adicionando esses pedidos à rota de entrega sem nota fiscal emitida?
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => { setShowInvoiceWarning(false); setOrdersWithoutInvoice([]); }}>
                  Cancelar
                </Button>
                <Button onClick={confirmAddSalesStops}>
                  Confirmar mesmo assim
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
